import React, {useCallback} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {BasicPage} from 'src/components/basic-page';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {PageNotFound} from 'src/modules/error-page';

import {BoxLoading} from '@pb/kit-component-box-loading';

import {DeleteSsoDraft} from './components/delete-sso-draft';
import {PostponeSsoDraft} from './components/postpone-sso-draft';
import {SsoDraftDetails} from './components/sso-draft-details';
import {useGetNotificationStatisticById} from './hooks/useGetNotificationStatisticById';
import {useGetSsoDraft} from './hooks/useGetSsoDraft';
import {isPostponeWarning} from './utils/is-postpone-warning';

import type {AccessRight} from 'src/hooks/access-rights/constants';

const PAGE_HEADER_ID = 'sso-settings-profile-header';
const PAGE_HEADER_SELECTOR = `#${PAGE_HEADER_ID}`;

export const SsoSettingsProfile = () => {
    const {accountId: accountIdParam} = useParams<{accountId: string}>();
    const accountId = Number(accountIdParam);
    const navigate = useNavigate();
    const {canEditSso} = useAdminAccessRight();
    const {data: draft, isLoading, loadData, loadingError} = useGetSsoDraft(accountId);
    const {
        data: notificationStatistics,
        loadData: loadNotificationStatistics,
    } = useGetNotificationStatisticById(draft?.id ?? 0);
    const reloadDraftAndStatistics = useCallback(async () => {
        await loadData();
        await loadNotificationStatistics();
    }, [loadData, loadNotificationStatistics]);

    if (!Number.isInteger(accountId) || accountId <= 0) {
        return <PageNotFound/>;
    }

    return (
        <ServerResponseHandler
            serverError={loadingError}
            accessRight={ACCESS_RIGHTS.SSO_ACCOUNT_STATISTICS as AccessRight}
            sectionName="SSO Settings Profile">
            <BasicPage
                title="SSO Settings Profile"
                subtitle={draft?.accountName}
                stickyHeaderId={PAGE_HEADER_ID}
                buttonsBlock={null}>
                <BoxLoading loading={isLoading}>
                    {draft ? (
                        <SsoDraftDetails
                            draft={draft}
                            notificationStatistics={notificationStatistics}
                            headerStickToElement={PAGE_HEADER_SELECTOR}
                            postponeAction={canEditSso(accountId) && isPostponeWarning(draft) ? (
                                <PostponeSsoDraft
                                    draftId={draft.id}
                                    onPostpone={reloadDraftAndStatistics}
                                />
                            ) : null}
                            deleteAction={canEditSso(accountId) ? (
                                <DeleteSsoDraft
                                    draftId={draft.id}
                                    onDeleted={() => navigate('/sso-accounts')}
                                />
                            ) : null}
                        />
                    ) : null}
                </BoxLoading>
            </BasicPage>
        </ServerResponseHandler>
    );
};
